import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  IoMailOutline,
  IoLockClosedOutline,
  IoShieldCheckmarkOutline,
} from "react-icons/io5";
import { MdAdminPanelSettings } from "react-icons/md";
import useAuth from "../useAuth";
import Loading from "../Components/global/Loading";
import { Container } from "@mui/material";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";

export default function LoginPage() {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({ mode: "onChange" });

  const { user, authMessage, loading, loginWithEmailAndPassword } = useAuth();

  const handleLogin = async (data) => {
    loginWithEmailAndPassword(data?.email, data?.password);
  };

  if (user) {
    return <Navigate to="/my" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center"
        >
          {/* Admin Header */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="mb-8 flex flex-col items-center"
          >
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-4 rounded-2xl shadow-lg mb-4">
              <MdAdminPanelSettings className="text-4xl text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Admin Portal
            </h1>
            <p className="text-gray-600 text-center">
              Secure access to the management dashboard
            </p>
          </motion.div>

          {/* Login Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="w-full max-w-md"
          >
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6">
                <div className="flex items-center justify-center space-x-3">
                  <IoShieldCheckmarkOutline className="text-2xl text-white" />
                  <h2 className="text-xl font-semibold text-white">
                    Administrator Login
                  </h2>
                </div>
                <p className="text-blue-100 text-sm text-center mt-2">
                  Enter your credentials to access the dashboard
                </p>
              </div>

              {/* Form Section */}
              <form
                onSubmit={handleSubmit(handleLogin)}
                className="p-8 space-y-6"
              >
                {/* Email Input */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div
                    className={`flex items-center border rounded-xl w-full px-4 py-3 transition-all duration-200 ${
                      errors?.email
                        ? "border-red-300 bg-red-50 ring-1 ring-red-200"
                        : "border-gray-300 hover:border-blue-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200"
                    }`}
                  >
                    <IoMailOutline className="text-gray-400 text-xl mr-3" />
                    <input
                      className="w-full outline-none bg-transparent text-gray-800 placeholder-gray-400"
                      placeholder="admin@example.com"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Please enter a valid email address",
                        },
                      })}
                    />
                  </div>
                  {errors?.email && (
                    <p className="text-red-600 text-sm mt-1 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password Input */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                      onClick={() =>
                        alert("This feature is not implemented yet")
                      }
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <div
                    className={`flex items-center border rounded-xl w-full px-4 py-3 transition-all duration-200 ${
                      errors?.password
                        ? "border-red-300 bg-red-50 ring-1 ring-red-200"
                        : "border-gray-300 hover:border-blue-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200"
                    }`}
                  >
                    <IoLockClosedOutline className="text-gray-400 text-xl mr-3" />
                    <input
                      type="password"
                      className="w-full outline-none bg-transparent text-gray-800 placeholder-gray-400"
                      placeholder="••••••••"
                      {...register("password", {
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters",
                        },
                      })}
                    />
                  </div>
                  {errors?.password && (
                    <p className="text-red-600 text-sm mt-1 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {authMessage && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4"
                  >
                    <div className="flex items-center text-red-700">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium">
                          {authMessage?.slice(5).split("-").join(" ")}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Submit Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={!isValid || loading}
                  className={`w-full py-3.5 px-4 rounded-xl font-semibold text-white transition-all duration-200 shadow-lg ${
                    !isValid || loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-2">Signing in...</span>
                    </div>
                  ) : (
                    "Sign In to Dashboard"
                  )}
                </motion.button>
              </form>

              {/* Security Footer */}
              <div className="border-t border-gray-200 bg-gray-50 px-8 py-4">
                <div className="flex items-center justify-center text-xs text-gray-500">
                  <IoShieldCheckmarkOutline className="mr-2" />
                  <span>Secure connection • Encrypted data transfer</span>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} Admin Panel • v2.0.1
              </p>
              <p className="text-xs text-gray-400 mt-1">
                For authorized personnel only
              </p>
            </div>
          </motion.div>
        </motion.div>

        {loading && <Loading />}
      </Container>
    </div>
  );
}
